import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useScrollToTop from '../hooks/useScrollToTop';
import { 
  useCourseApi, 
  QuizQuestion,
  isQuizContent,
} from '../hooks/useCourseApi';
import { submitQuizResult } from '../services/courseService';

const LessonQuizPlayer: React.FC = () => {
  useScrollToTop();
  const { courseId, lessonId } = useParams<{ courseId: string; lessonId: string }>();
  const navigate = useNavigate();
  const { fetchLessonDetails } = useCourseApi();
  const [quizData, setQuizData] = useState<QuizQuestion[] | null>(null);
  const [lessonName, setLessonName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Quiz state
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  // Load lesson details (quiz questions)
  useEffect(() => {
    const loadQuizData = async () => {
      if (lessonId) {
        try {
          setLoading(true);
          console.log('Fetching quiz details for lesson ID:', lessonId);
          const response = await fetchLessonDetails(parseInt(lessonId));
          console.log('Quiz details response:', response);
          
          if (response?.success && isQuizContent(response.data)) {
            setQuizData(response.data.questions);
            // Try to get lesson name from the first question or set a default
            setLessonName('Quiz Lesson');
          } else {
            console.error('Failed to load quiz details:', response);
            setError('فشل في تحميل تفاصيل الاختبار');
          }
        } catch (err) {
          console.error('Error loading quiz:', err);
          setError('حدث خطأ أثناء تحميل الاختبار');
        } finally {
          setLoading(false);
        }
      }
    };

    loadQuizData();
  }, [lessonId, fetchLessonDetails]);

  const handleAnswerSelect = (answerIndex: number) => {
    setSelectedAnswers(prev => {
      const newAnswers = [...prev];
      newAnswers[currentQuestion] = answerIndex;
      return newAnswers;
    });
  };

  const calculateScore = () => {
    if (!quizData) return { correct: 0, total: 0, percentage: 0 };
    
    let correct = 0;
    selectedAnswers.forEach((selectedIndex, questionIndex) => {
      if (quizData[questionIndex]?.answers[selectedIndex]?.isCorrect) {
        correct++;
      }
    });
    
    const total = quizData.length;
    const percentage = total > 0 ? (correct / total) * 100 : 0;
    
    return { correct, total, percentage };
  };

  const handleNext = async () => {
    if (currentQuestion < (quizData?.length || 0) - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      // Submit quiz
      setSubmitting(true);
      setError(null);

      const score = calculateScore();
      console.log('Quiz completed with score:', score);
      
      try {
        await submitQuizResult({
          lessonId: parseInt(lessonId!),
          score: score.percentage,
          notes: `Correct answers: ${score.correct}/${score.total}`
        });
        
        // Signal curriculum page to refresh
        localStorage.setItem('course_data_updated', Date.now().toString());
        console.log('Quiz completed, signaling curriculum refresh...');
        
        setShowResults(true);
        setIsCompleted(true);
        setShowSuccessPopup(true);
        
        // Auto-dismiss success popup after 2 seconds
        setTimeout(() => {
          setShowSuccessPopup(false);
        }, 2000);
      } catch (err: any) {
        setError(err.message || 'Failed to submit quiz results');
        console.error('Failed to submit quiz results:', err);
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleShowAnswers = () => {
    setShowAnswers(!showAnswers);
  };

  const goBackToCurriculum = () => {
    navigate(`/course/${courseId}/curriculum`);
  };

  const handleCompleteQuiz = () => {
    // Show success message and navigate back to curriculum
    setTimeout(() => {
      navigate(`/course/${courseId}/curriculum`);
    }, 2000); // Increased to 2 seconds to show the message
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-slate-600">جاري تحميل الاختبار...</p>
        </div>
      </div>
    );
  }

  if (error || !quizData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center p-8 max-w-md">
          <div className="text-6xl mb-4">📝</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">الاختبار غير موجود</h2>
          <p className="text-gray-600 mb-6">{error || 'الاختبار الذي تبحث عنه غير موجود.'}</p>
          <button 
            onClick={goBackToCurriculum}
            className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors duration-200"
          >
            العودة للمنهج
          </button>
        </div>
      </div>
    );
  }

  const currentQuestionData = quizData[currentQuestion];
  const score = calculateScore();
  const isLastQuestion = currentQuestion === quizData.length - 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Top Navigation */}
      <div className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={goBackToCurriculum}
              className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              العودة للمنهج
            </button>
            
            <div className="text-center">
              <h1 className="text-lg font-semibold text-slate-900">{lessonName}</h1>
              <p className="text-sm text-slate-500">تقييم الاختبار</p>
            </div>
            
            <div className="w-32"></div> {/* Spacer for centering */}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {!showResults ? (
          /* Quiz Questions */
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
            {/* Progress Bar */}
            <div className="mb-8">
              <div className="flex items-center justify-between text-sm text-slate-600 mb-2">
                <span>السؤال {currentQuestion + 1} من {quizData.length}</span>
                <span>{Math.round(((currentQuestion + 1) / quizData.length) * 100)}% مكتمل</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-indigo-500 h-2 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${((currentQuestion + 1) / quizData.length) * 100}%` }}
                />
              </div>
            </div>

            {/* Question */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-6" dir="rtl">
                {currentQuestionData.text}
              </h2>

              {/* Answer Options */}
              <div className="space-y-3">
                {currentQuestionData.answers.map((answer, index) => (
                  <button
                    key={index}
                    onClick={() => handleAnswerSelect(index)}
                    className={`w-full p-4 text-right rounded-xl border-2 transition-all duration-200 ${
                      selectedAnswers[currentQuestion] === index
                        ? 'border-blue-500 bg-blue-50 text-blue-900'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                    dir="rtl"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-slate-700">{answer.text}</span>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedAnswers[currentQuestion] === index
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-slate-300'
                      }`}>
                        {selectedAnswers[currentQuestion] === index && (
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between">
              <button
                onClick={handlePrevious}
                disabled={currentQuestion === 0}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                  currentQuestion === 0
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                السابق
              </button>

              <button
                onClick={handleNext}
                disabled={selectedAnswers[currentQuestion] === undefined || submitting}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                  selectedAnswers[currentQuestion] === undefined || submitting
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white hover:from-blue-600 hover:to-indigo-600'
                }`}
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    جاري الإرسال...
                  </>
                ) : isLastQuestion ? (
                  <>
                    إرسال الاختبار
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </>
                ) : (
                  <>
                    التالي
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </>
                )}
              </button>
            </div>

            {error && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}
          </div>
        ) : (
          /* Quiz Results */
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
            <div className="text-center mb-8">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-r from-green-400 to-green-500 flex items-center justify-center">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">تم إكمال الاختبار!</h2>
              <p className="text-slate-600">أحسنت! إليك نتائجك:</p>
            </div>

            {/* Score Display */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 mb-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold text-blue-600 mb-1">{score.percentage.toFixed(0)}%</div>
                  <div className="text-sm text-slate-600">الدرجة الإجمالية</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-green-600 mb-1">{score.correct}</div>
                  <div className="text-sm text-slate-600">الإجابات الصحيحة</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-slate-600 mb-1">{score.total}</div>
                  <div className="text-sm text-slate-600">إجمالي الأسئلة</div>
                </div>
              </div>
            </div>

            {/* Performance Message */}
            <div className="text-center mb-8">
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
                score.percentage >= 80 ? 'bg-green-100 text-green-800' :
                score.percentage >= 60 ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {score.percentage >= 80 ? (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    أداء ممتاز!
                  </>
                ) : score.percentage >= 60 ? (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    عمل جيد!
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    استمر في الممارسة!
                  </>
                )}
              </div>
            </div>

            {/* Show Answers Button */}
            <div className="text-center mb-8">
              <button
                onClick={handleShowAnswers}
                className="inline-flex items-center gap-2 px-6 py-3 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                {showAnswers ? 'إخفاء الإجابات' : 'مراجعة الإجابات'}
              </button>
            </div>

            {/* Detailed Answers */}
            {showAnswers && (
              <div className="space-y-6 mb-8">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">مراجعة الإجابات</h3>
                {quizData.map((question, qIndex) => (
                  <div key={qIndex} className="border border-slate-200 rounded-lg p-4">
                    <h4 className="font-medium text-slate-900 mb-3" dir="rtl">
                      {qIndex + 1}. {question.text}
                    </h4>
                    <div className="space-y-2">
                      {question.answers.map((answer, aIndex) => (
                        <div 
                          key={aIndex}
                          className={`p-2 rounded border ${
                            answer.isCorrect
                              ? 'border-green-500 bg-green-50 text-green-800' 
                              : selectedAnswers[qIndex] === aIndex && !answer.isCorrect
                              ? 'border-red-500 bg-red-50 text-red-800'
                              : 'border-slate-200 bg-slate-50'
                          }`}
                          dir="rtl"
                        >
                          <div className="flex items-center justify-between">
                            <span>{answer.text}</span>
                            <div className="flex items-center gap-2">
                              {answer.isCorrect && (
                                <span className="text-xs bg-green-500 text-white px-2 py-1 rounded">صحيح</span>
                              )}
                              {selectedAnswers[qIndex] === aIndex && !answer.isCorrect && (
                                <span className="text-xs bg-red-500 text-white px-2 py-1 rounded">إجابتك</span>
                              )}
                              {selectedAnswers[qIndex] === aIndex && answer.isCorrect && (
                                <span className="text-xs bg-green-500 text-white px-2 py-1 rounded">إجابتك ✓</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleCompleteQuiz}
                className="px-8 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-colors font-medium"
              >
                إكمال والعودة للمنهج
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Success Message */}
      {showSuccessPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-lg px-10 py-8 text-center animate-bounce">
            <div className="text-4xl mb-4">🎉</div>
            <h3 className="text-2xl font-bold text-green-600 mb-2">تم إنهاء الاختبار!</h3>
            <p className="text-slate-600">أحسنت، درجتك: {score.percentage.toFixed(0)}%</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LessonQuizPlayer;
